import { gql } from '@apollo/client';

/**
 * Paginated rows: `pageInfo` omits `totalCount` so the API skips the expensive count (selection-set driven).
 * Use CERTIFICATES_TOTAL_COUNT when you need the total row count.
 */
export const CERTIFICATES_LIST = gql`
  query certificatesList($args: CertificatesInput!, $includeReachabilityV2: Boolean = false) {
    certificates(args: $args) {
      pageInfo {
        startCursor
        endCursor
      }
      edges {
        node {
          filePath
          sha256
          isReachable @include(if: $includeReachabilityV2)
          sha1
          signature
          effectivePermissions
          subjectDN
          issuerDN
          serial
          startDate
          expireDate
          bitSize
          publicKeyAlgorithm
          signatureAlgorithm
          uniqueHash
          publicKeyMatchHash
          e
          curve
          selfSigned
          isCa
          expired
          invalidities
          invaliditiesCount
          algorithmType
          invalid
          fileOffset
          version
          privKeyCompromised
          # Issuer detail
          issuerSerialNumber
          issuerCountry
          issuerOrganization
          issuerCommonName
          issuerOrgUnit
          # Subject details
          subjectSerialNumber
          subjectCommonName
          subjectOrganization
          subjectOrgUnit
          subjectCountry
          subjectProvince
          subjectLocality
          # Extensions
          keyUsage
          issuingCertUrl
          ocspServer
          crlDistributionPoints
          # Subject Alternative Names
          altDnsNames
          altEmailAddresses
          altIps
          altUris
          correlationsCount
          currentRemediation {
            certificate {
              filePath
              sha256
            }
            status
            description
            author
            createdAt
          }
        }
      }
    }
  }
`;

/** Same `CertificatesInput` as the list; selection includes only `pageInfo { totalCount }`. */
export const CERTIFICATES_TOTAL_COUNT = gql`
  query certificatesTotalCount($args: CertificatesInput!) {
    certificates(args: $args) {
      pageInfo {
        totalCount
      }
    }
  }
`;

/** Alias of CERTIFICATES_LIST for refetchQueries and existing imports. */
export const CERTIFICATES = CERTIFICATES_LIST;

export const CERTIFICATE_EXTERNAL_FILTER = gql`
  query certificateExternalFilters($args: CertificateExternalFiltersInput!) {
    certificateExternalFilters(args: $args) {
      totalClassical
      totalPqc
    }
  }
`;

export const CERTIFICATE_DETAILS = gql`
  query certificateDetails($args: CertificatesInput!, $includeReachabilityV2: Boolean = false) {
    certificates(args: $args) {
      edges {
        node {
          filePath
          sha256
          isReachable @include(if: $includeReachabilityV2)
          sha1
          signature
          effectivePermissions
          subjectSerialNumber
          startDate
          expireDate
          bitSize
          publicKeyAlgorithm
          signatureAlgorithm
          uniqueHash
          publicKeyMatchHash
          e
          curve
          selfSigned
          isCa
          expired
          invalidities
          invaliditiesCount
          algorithmType
          fileOffset
          version
          privKeyCompromised
          issuerCountry
          issuerOrganization
          issuerCommonName
          issuerOrgUnit
          subjectCommonName
          subjectOrganization
          subjectOrgUnit
          subjectCountry
          subjectProvince
          subjectLocality
          keyUsage
          issuingCertUrl
          ocspServer
          crlDistributionPoints
          altDnsNames
          altEmailAddresses
          altIps
          altUris
          currentRemediation {
            certificate {
              filePath
              sha256
            }
            status
            description
            author
            createdAt
          }
        }
      }
    }
  }
`;

export const REMEDIATE_CERTIFICATES = gql`
  mutation remediateCertificates($args: RemediateCertificatesInput!) {
    remediateCertificates(args: $args) {
      certificate {
        filePath
        sha256
      }
      status
      description
      author
      createdAt
      errorMessage
    }
  }
`;

export const GET_CERTIFICATE_REACHABILITY = gql`
  query getCertificateReachability($args: GetCertificateReachabilityInput!) {
    getCertificateReachability(args: $args) {
      user
      entryPoint
      entryType
      scripts {
        path
        invocation
        edgeType
        detail
      }
    }
  }
`;
